/**
 * Professional Instrument List Component
 * Clean market data display with intuitive trading interface
 */

import { useEffect, useState, useMemo, memo } from 'react'
import { useMarketStore } from '@/stores/useMarketStore'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { Input } from './ui/input'
import { Badge } from './ui/badge'
import { Button } from './ui/button'
import { Skeleton } from './ui/loading-spinner'
import { formatCurrency, formatPercent } from '@/lib/utils'
import { 
  Search, 
  Star, 
  Globe, 
  Filter,
  BarChart3,
  Zap,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react'
import type { Instrument } from '@/types'

interface InstrumentListProps {
  onSelectInstrument?: (instrument: Instrument) => void
}

export const InstrumentList = memo(function InstrumentList({ onSelectInstrument }: InstrumentListProps) {
  const { instruments, watchlist, isLoading, loadInstruments, loadWatchlist, addToWatchlist, removeFromWatchlist } = useMarketStore()
  const [searchQuery, setSearchQuery] = useState('')
  const [filter, setFilter] = useState<'all' | 'crypto' | 'forex' | 'commodities'>('all')

  useEffect(() => {
    loadInstruments()
    loadWatchlist()
  }, [loadInstruments, loadWatchlist])

  // Debounced search query
  const [debouncedQuery, setDebouncedQuery] = useState('')
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery)
    }, 300)
    
    return () => clearTimeout(timer)
  }, [searchQuery])

  const filteredInstruments = useMemo(() => instruments.filter((instrument) => {
    const matchesSearch = instrument.name.toLowerCase().includes(debouncedQuery.toLowerCase()) ||
                         instrument.epic.toLowerCase().includes(debouncedQuery.toLowerCase())
    
    if (filter === 'all') return matchesSearch
    
    const typeMap: Record<string, string[]> = {
      crypto: ['CRYPTOCURRENCIES', 'CRYPTO'],
      forex: ['CURRENCIES', 'FOREX', 'FX'],
      commodities: ['COMMODITIES', 'METALS', 'ENERGIES'],
    }
    
    return matchesSearch && typeMap[filter]?.some(t => instrument.type.toUpperCase().includes(t))
  }), [instruments, debouncedQuery, filter])

  const isInWatchlist = (epic: string) => {
    return watchlist.some(item => item.instrument === epic)
  }

  const toggleWatchlist = async (epic: string, e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      if (isInWatchlist(epic)) {
        await removeFromWatchlist(epic)
      } else {
        await addToWatchlist(epic)
      }
    } catch (error) {
      console.error('Failed to toggle watchlist:', error)
    }
  }

  const getPriceChange = (instrument: Instrument) => {
    // Guard clause: handle null, undefined, or zero values
    if (instrument.offer === null || instrument.offer === undefined) {
      return 0
    }
    if (instrument.low === null || instrument.low === undefined || instrument.low === 0) {
      return 0
    }
    
    const change = ((instrument.offer - instrument.low) / instrument.low) * 100
    return change
  }

  if (isLoading && instruments.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Markets</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-20 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="bg-card/50 backdrop-blur-sm border-border h-fit max-h-[600px] flex flex-col">
      <CardHeader className="pb-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 sm:gap-3">
            <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-br from-[hsl(142,76%,45%)] to-[hsl(142,76%,40%)] rounded-lg flex items-center justify-center">
              <Globe className="h-3 w-3 sm:h-5 sm:w-5 text-black" />
            </div>
            <span className="text-base sm:text-lg">Global Markets</span>
          </CardTitle>
          <Badge variant="outline" className="text-xs border-border">
            {filteredInstruments.length}
          </Badge>
        </div>
        
        <div className="space-y-3 sm:space-y-4 mt-4 sm:mt-6">
          {/* Enhanced Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search markets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-9 sm:h-11 bg-background border-border focus:border-primary text-sm"
            />
          </div>

          {/* Professional Filters */}
          <div className="flex items-center gap-2 sm:gap-3 overflow-x-auto pb-2">
            <div className="flex items-center gap-1 text-xs sm:text-sm text-muted-foreground whitespace-nowrap">
              <Filter className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Filter:</span>
            </div>
            {(['all', 'crypto', 'forex', 'commodities'] as const).map((f) => (
              <Button
                key={f}
                variant={filter === f ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter(f)}
                className={`capitalize whitespace-nowrap h-7 sm:h-8 text-xs ${
                  filter === f 
                    ? 'bg-gradient-to-r from-[hsl(142,76%,45%)] to-[hsl(142,76%,40%)] text-black border-0' 
                    : 'hover:bg-accent border-border'
                }`}
              >
                {f === 'all' ? 'All' : f}
              </Button>
            ))}
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="flex-1 min-h-0 overflow-y-auto scrollbar-thin">
        {filteredInstruments.length === 0 ? (
          <div className="text-center py-8 sm:py-12">
            <BarChart3 className="h-8 w-8 sm:h-12 sm:w-12 mx-auto mb-3 sm:mb-4 text-muted-foreground" />
            <div className="text-sm sm:text-base text-foreground font-medium">
              {searchQuery ? 'No instruments found' : 'No instruments available'}
            </div>
            <div className="text-xs sm:text-sm text-muted-foreground mt-1">
              {searchQuery ? 'Try adjusting your search terms' : 'Market data is loading...'}
            </div>
          </div>
        ) : (
          <div className="space-y-2 sm:space-y-3 pb-4">
            {filteredInstruments.map((instrument) => {
              const priceChange = getPriceChange(instrument)
              const isPositive = priceChange >= 0

              return (
                <div
                  key={instrument.epic}
                  className="group relative p-3 sm:p-4 rounded-xl bg-card border border-border hover:shadow-lg hover:border-primary/30 cursor-pointer transition-all duration-200"
                  onClick={() => onSelectInstrument?.(instrument)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                      {/* Watchlist Star */}
                      <button
                        onClick={(e) => toggleWatchlist(instrument.epic, e)}
                        className="flex-shrink-0 p-1 rounded-lg hover:bg-accent transition-colors"
                      >
                        <Star
                          className={`h-3 w-3 sm:h-4 sm:w-4 transition-colors ${
                            isInWatchlist(instrument.epic)
                              ? 'fill-violet-500 text-violet-500'
                              : 'text-muted-foreground hover:text-violet-500'
                          }`}
                        />
                      </button>
                      
                      {/* Instrument Info */}
                      <div className="flex-1 min-w-0 flex flex-col justify-center">
                        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-1">
                          <div className="font-bold text-foreground text-sm sm:text-base truncate max-w-[100px] sm:max-w-xs">{instrument.epic}</div>
                          <Badge 
                            variant="outline" 
                            className="text-[9px] sm:text-[10px] px-1 sm:px-1.5 py-0.5 bg-muted border-border truncate max-w-[90px] sm:max-w-[120px]"
                          >
                            {instrument.type}
                          </Badge>
                        </div>
                        <div className="text-[10px] sm:text-xs text-muted-foreground truncate">
                          {instrument.name}
                        </div>
                      </div>
                    </div>

                    {/* Price & Change */}
                    <div className="text-right ml-2 sm:ml-4 flex-shrink-0">
                      <div className="text-sm sm:text-lg font-bold text-foreground mb-1">
                        {formatCurrency(instrument.offer)}
                      </div>
                      <div className={`flex items-center justify-end gap-1 text-xs sm:text-sm font-medium ${
                        isPositive ? 'text-[hsl(142,76%,45%)]' : 'text-red-400'
                      }`}>
                        {isPositive ? (
                          <ArrowUpRight className="h-3 w-3 sm:h-4 sm:w-4" />
                        ) : (
                          <ArrowDownRight className="h-3 w-3 sm:h-4 sm:w-4" />
                        )}
                        {formatPercent(priceChange, 2)}
                      </div>
                    </div>
                  </div>

                  {/* Quick Trade Overlay - Show on Hover */}
                  <div className="absolute inset-0 bg-background/60 backdrop-blur-[2px] rounded-xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 gap-3 scale-95 group-hover:scale-100">
                      <Button
                        onClick={(e) => {
                          e.stopPropagation()
                          onSelectInstrument?.(instrument)
                        }}
                        className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold shadow-[0_0_20px_rgba(var(--primary),0.4)] hover:scale-105 transition-transform px-6"
                      >
                        <Zap className="h-4 w-4 mr-2 animate-pulse" />
                        Trade
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation()
                          toggleWatchlist(instrument.epic, e)
                        }}
                        className="bg-background/80 hover:bg-accent border-border shadow-lg hover:scale-105 transition-transform"
                      >
                        <BarChart3 className="h-4 w-4" />
                      </Button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
})
